import React, { useState } from 'react';
import {
    TableBody as MuiTableBody,
    TableRow,
    TableCell,
    Tooltip,
    IconButton,
    Chip,
    Box,
    Typography,
    Link,
    Checkbox,
    styled,
    Stack
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import Eye from '@mui/icons-material/RemoveRedEyeOutlined';
import { ModeEditOutlineOutlined as Edit} from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import DownloadIcon from '@mui/icons-material/Download';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

import { CommonGridPhoneNumber, currencyFormat, YMD_TO_MDY } from '../../../utilities/commonUtilities';
import i18n from '../../../utilities/i18n';
import TableWarningPopUp from '../tableWarningPopUp';
import api from '../../../service/api';
import Chips from '../Chips';
import { reportStatusChip } from '../reportStatusChip';
import AnimationLoader from '../loader/AnimationLoader';
import { STATUS_OPTIONS } from '../../../utilities/dictionaryConstants';
import CustomizedSmallDialogs from '../../modalWindowComponent/CustomisedSmallDialog';

const StyledTableRow = styled(TableRow, {
    shouldForwardProp: (prop) => !['isSelected', 'isUnread', 'isLastOpened'].includes(prop),
})(({ theme, isSelected, isUnread, isLastOpened }) => ({
    backgroundColor: isSelected
        ? theme.palette.table.selected
        : isLastOpened
            ? theme.palette.action.hover
            : theme.palette.background.paper,
    fontWeight: isUnread ? theme.typography.fontWeightBold : theme.typography.fontWeightRegular,
    '&:hover': {
        backgroundColor: theme.palette.table.hover,
    },
    '& .MuiTableCell-root': {
        fontWeight: isUnread ? theme.typography.fontWeightBold : 'inherit',
    }
}));

const StyledTableCell = styled(TableCell, {
    shouldForwardProp: (prop) => prop !== 'isUnreadSideBar',
})(({ theme, isUnreadSideBar }) => ({
    padding: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    ...(isUnreadSideBar && {
        borderLeft: `4px solid ${theme.palette.primary.main}`,
    })
}));

const StatusIndicator = styled(Box)(({ theme, status }) => {
    const getColor = () => {
        if (status === "Generating") return theme.palette.warning.main;
        if (status === "Failed") return theme.palette.error.main;
        return theme.palette.success.main;
    };
    return {
        height: 10,
        width: 10,
        borderRadius: '50%',
        backgroundColor: getColor(),
    };
});

const getWorkflowConfig = (theme, status) => {
    const workflowPalette = theme?.palette?.workflow || {};
    const normalizedStatus = String(status || '').toLowerCase();

    const statusMap = {
        'to-do': workflowPalette.todo || {},
        'in progress': workflowPalette.inProgress || {},
        'completed': workflowPalette.completed || {},
        'blocked': workflowPalette.blocked || {},
    };

    return statusMap[normalizedStatus] || workflowPalette.todo || {};
};

const ActionIconButton = styled(IconButton)(({ theme }) => ({
    margin: theme.spacing(0, 0.5),
    color: theme.palette.action.active,
    '&:hover': {
        color: theme.palette.primary.hover,
    }
}));

const CustomLink=styled(Link)(() => ({
    paddingLeft:'0px !important'
}));


const TableBody = (props) => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [payerData, setPayerData] = useState('');
    const [errorModal, setErrorModal] = useState({ open: false, message: "" });
    
    const handleShowError = (error) => {
        // If 'error' is actually an event object from the click
        if (error?.stopPropagation) error.stopPropagation();
        setErrorModal({
            open: true,
            message: typeof error === 'string' ? error : JSON.stringify(error)
        });
    };

    const handleCloseModal = () => setErrorModal({ open: false, message: "" });

    const findPatientIndexOfSelfPay = (arr) => {
        if (!Array.isArray(arr) || arr.length === 0 || !Array.isArray(arr[0])) return [];
        const patientIndex = [];
        if (arr[0].some(obj => obj.name === "payer_types" || obj.name === "responsible_type")) {
            arr.forEach((row, i) => {
                const isPatient = row.some(cell =>
                    (cell.name === "payer_types" && cell.value?.type === "patient") ||
                    (cell.name === "responsible_type" && (cell.value === "Patient" || cell.value === ""))
                );
                if (isPatient) patientIndex.push(i);
            });
        }
        return patientIndex;
    };

    const selfPayPatientIndex = findPatientIndexOfSelfPay(props.tableBodyData);

    const getPayerDetails = async (id) => {
        try {
            setLoading(true);
            const response = await api.get(`practice/practice-responsible-payer/${id}`);
            setPayerData(response.data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const checkIsICDAlreadyInList = (icdList, item) => {
        for (let i = 1; i <= 12; i++) {
            if (icdList[`diagnosis${i}List`][0]?.name === item[0].value) return true;
        }
        return false;
    };

    const checkIsCPTAlreadyInList = (procedure, item) => {
        return Object.values(procedure).some(p => p.cpt_hcpcsList[0]?.name === item[0].value);
    };

    const ActionButton = ({ icon, onClick, disabled = false }) => (
        <IconButton
            size="small"
            onClick={onClick}
            disabled={disabled}
            sx={{
                color: "white",
                backgroundColor: theme.palette.grey[600],
                width: 32,
                height: 32,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    backgroundColor: theme.palette.grey[800],
                    transform: 'scale(1.1)',
                },
                '&.Mui-disabled': {
                    backgroundColor: theme.palette.action.disabled,
                }
            }}
        >
            <i className={icon}></i>
        </IconButton>
    );

    return (
        <MuiTableBody>
            {props.tableBodyData?.map((item, index) => {
                const rowId = item[0]?.id;
                if (!rowId) {
                    return (
                        <TableRow key={index}>
                            <TableCell colSpan={item.length} align="center">
                                {props.noRecordsMsg}
                            </TableCell>
                        </TableRow>
                    );
                }

                const isSelected = rowId === item[0].clickOnRowCondition;
                const isUnread = props.defaultBold === rowId;
                const isLastOpened = rowId === props.rowBackgroundChange;

                // Check if this row contains a checkbox cell
                const hasCheckboxCell = item.some((cell) => cell.type === 'checkbox' || cell.name === 'checkbox');

                return (
                    <React.Fragment key={rowId}>
                        <StyledTableRow
                            isSelected={isSelected || (item[0]?.name=="checkbox" && !!item[0]?.value)}
                            isUnread={isUnread}
                            isLastOpened={isLastOpened}
                            onClick={(props.tableType === "clickOnRowsTable" && rowId !== item[0].clickOnRowCondition) ? (e) => props.onRowClick(e, rowId) : null}
                        >
                            {item.filter(td => td.name !== 'payer_types').map((td, idx) => {
                                if (td.hideValue) return null;

                                return (
                                    <StyledTableCell
                                        key={idx}
                                        align={td.align || 'left'}
                                        isUnreadSideBar={td.is_read === false}
                                        onClick={td.name === "expand" ? () => props.onExpandClick(td.id, td.name) : null}
                                        sx={{
                                            width: td.width,
                                            // Add sticky behavior for the Checkbox column (index 0 / left: 0)
                                            ...((td.type === 'checkbox' || td.name === 'checkbox') && {
                                                position: 'sticky',
                                                left: 0,
                                                zIndex: 2,
                                                minWidth: '60px',
                                                width: '60px',
                                                backgroundColor: 'inherit',
                                            }),
                                            // Add sticky behavior for the Claim ID column (index 1 / left: 48px)
                                            ...((td.type === 'claim_stage_id' || td.name === 'custom_claim_id' || td.name === 'claim_id') && {
                                                position: 'sticky',
                                                left: hasCheckboxCell ? '60px' : 0,
                                                zIndex: 2,
                                                backgroundColor: 'inherit',
                                            }),
                                            ...((td.name === 'action' || td.name === 'mywork_action') && {
                                                position: 'sticky',
                                                right: 0,
                                                zIndex: 1,
                                                // Inherits row backgrounds correctly on hover/select states
                                                backgroundColor: 'inherit',
                                                // A box shadow fallback helps if background transparency acts up over borders
                                                boxShadow: 'inset -1px 0px 0px rgba(0, 0, 0, 0.1)',
                                            })
                                        }}
                                    >
                                        {(() => {
                                            switch (td.name) {
                                                case "action":
                                                    return td.dropdown.map((list, dIdx) => {
                                                        const icons = {
                                                            'Edit': <Edit sx={{fontSize:20}}/>,
                                                            'View': <Eye sx={{ fontSize: 20 }} />,
                                                            // 'Edit': <EditIcon sx={{fontSize:20}}/>,
                                                            'Delete': <DeleteIcon sx={{fontSize:20}}/>,
                                                            'Set as default': <StarIcon sx={{fontSize:20}}/>,
                                                            'Download': <DownloadIcon sx={{fontSize:20}}/>,
                                                            'Download Batch File': <DownloadIcon sx={{fontSize:20}}/>,
                                                            'Download Statement PDF': <PictureAsPdfRoundedIcon sx={{fontSize:20}}/>,
                                                            'Clone': <ContentCopyRoundedIcon sx={{fontSize:20}}/>
                                                        };
                                                        return (
                                                            <Tooltip title={list.name} key={dIdx}>
                                                                <span>
                                                                    <ActionIconButton
                                                                        size="small"
                                                                        onClick={() => props.dropDownFunction(td.id, list.name)}
                                                                        disabled={isUnread && list.name === 'Set as default'}
                                                                    >
                                                                        {icons[list.name]}
                                                                    </ActionIconButton>
                                                                </span>
                                                            </Tooltip>
                                                        );
                                                    });
                                                case "mywork_action":
                                                    return td.dropdown.map((list, dIdx) => {
                                                        const icons = {
                                                            'My Work Edit': <Edit sx={{ fontSize: 20 }} />,
                                                        };
                                                        return (
                                                            <Tooltip title={list.name} key={dIdx}>
                                                                <span>
                                                                    <ActionIconButton
                                                                        size="small"
                                                                        onClick={() => props?.onMyWorkLinkClick(td?.custom_claim_id, td?.name,td?.id,td?.work_status,td?.isAssigned)}
                                                                        disabled={isUnread && list.name === 'Set as default'}
                                                                    >
                                                                        {icons[list.name]}
                                                                    </ActionIconButton>
                                                                </span>
                                                            </Tooltip>
                                                        );
                                                    });
                                            }
                                            switch (td.type) {
                                                case "currency": return currencyFormat(td.value);
                                                case "color_code": return <StatusIndicator status={td.value} />;
                                                case "report_chip": return reportStatusChip(td.value, td?.errors, handleShowError);
                                                case "filters": return <IconButton onClick={() => props.showAppliedFilters(td)}><FilterAltOutlinedIcon /></IconButton>;
                                                case "Date": return td?.needDateFormat === "yes" ? YMD_TO_MDY(td.value) : td.value;
                                                case "phone": return CommonGridPhoneNumber(td.value);
                                                case "boolean": return td.value ? td.booleanCondition.split(' ')[0] : td.booleanCondition.split(' ')[1];
                                                case "is_deleted": return STATUS_OPTIONS.find((s) => s.value === td.value)?.label;
                                                case "download": return <CustomLink
                                                    href={td.directLinkPath}
                                                    download={td.value}
                                                    component="a"
                                                    underline="hover"
                                                >
                                                    {td.value}
                                                </CustomLink>;
                                                case "report_download":
                                                    return td.value === "Generated" ? (
                                                        <IconButton onClick={() => props.downloadReport(td.id)}><DownloadIcon /></IconButton>
                                                    ) : td.value === "No Data" ? "No Data" : null;

                                                case "reference_id":
                                                    return (
                                                        <Box display="flex" alignItems="center" gap={0.5}>
                                                            {td.value}
                                                            <Tooltip title="Copy">
                                                                <IconButton size="small" onClick={() => navigator.clipboard.writeText(td.value)}>
                                                                    <ContentCopyIcon fontSize="inherit" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    );

                                                case "responsiblePayer": {
                                                    const isSelfPay = selfPayPatientIndex.includes(index);
                                                    const payerName = td?.value?.name || (typeof td.value === 'string' ? td.value : '');

                                                    if (td.directLinkPath === "" && td.value !== "Paid") {
                                                        return (
                                                            <CustomLink component="button" variant="body2" underline="none" sx={{ textAlign: 'left', p: 0, textTransform: 'uppercase' }} onClick={() => props?.onLinkClick?.(td?.id, td?.name)}>
                                                                {payerName.toUpperCase()}
                                                            </CustomLink>
                                                        );
                                                    }
                                                    if (!isSelfPay && payerName && payerName !== "Paid") {
                                                        return (
                                                            <Tooltip
                                                                onOpen={() => getPayerDetails(td.directLinkState?.selectedID)}
                                                                title={loading ? "Loading..." : (
                                                                    <Box sx={{ p: 1, maxWidth: 300 }}>
                                                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{payerData?.name}</Typography>
                                                                        {payerData?.payer_id && <Typography variant="caption" display="block"><strong>Payer ID:</strong> {payerData.payer_id}</Typography>}
                                                                        {(payerData?.street1 || payerData?.street2) && <Typography variant="caption" display="block"><strong>Address:</strong> {`${payerData?.street1 || ''} ${payerData?.street2 || ''}`}</Typography>}
                                                                        {payerData?.phone && <Typography variant="caption" display="block"><strong>Phone:</strong> {payerData?.phone}</Typography>}
                                                                    </Box>
                                                                )}
                                                            >
                                                                <Link sx={{ cursor: 'pointer', borderBottom: '1px dashed', display: 'inline-block', textTransform: 'uppercase' }}>
                                                                    {payerName?.toUpperCase()}
                                                                </Link>
                                                            </Tooltip>
                                                        );
                                                    }
                                                    return <Typography variant="body2">{isSelfPay ? 'Patient' : payerName}</Typography>;
                                                }

                                                case "checkbox":
                                                    return <Checkbox size="small" checked={!!td.value} onChange={(e) => props.onClickGridCheckBox(e, 'data', td.id,td.status ?? "")} onClick={(e) => e.stopPropagation()} disabled={props.checkboxEnable} />;

                                                case "warning":
                                                    return td.is_warnings ? (
                                                        (props.isLoading.loading && props.isLoading.id === td.id && td.value.length === 0) ? <AnimationLoader /> : (
                                                            <span onMouseOver={(e) => props.onMouseOverWarning(e, td.id)}>
                                                                <TableWarningPopUp notifyDescription={i18n.t("warning.incompleteData")} formWarningData={td.value} formWarningStatus={td.status} />
                                                            </span>
                                                        )
                                                    ) : '';

                                                case "exportOptionsHCFA":
                                                    return td.value.map((oltItem, oltIndex) => {
                                                        const IconComponent = oltItem.IconComponent;
                                                        return (
                                                            <Tooltip
                                                                key={oltIndex}
                                                                title={oltItem.toolTipLabel}
                                                                placement="bottom"
                                                                arrow
                                                                enterDelay={250}
                                                                leaveDelay={400}
                                                                componentsProps={{
                                                                    tooltip: {
                                                                        sx: {
                                                                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                                                        },
                                                                    },
                                                                }}
                                                            >
                                                                <span
                                                                    className="grid-icon-span"
                                                                    onClick={(e) => props.exportFunction(e, oltItem.secondParameter, oltItem.thirdParameter)}
                                                                    style={{ cursor: 'pointer', display: 'inline-block', margin: '0 5px' }}
                                                                >
                                                                    {IconComponent ? (
                                                                        <IconComponent sx={{ fontSize: 20,color: oltItem.thirdParameter === 'yes' ? theme.palette.error.lightRed : theme.palette.primary.lightBlue }} />
                                                                    ) : (
                                                                        <i className={oltItem.fileIconClass}
                                                                            style={{ fontSize: '20px',color: oltItem.thirdParameter === 'yes' ? theme.palette.error.lightRed : theme.palette.primary.lightBlue }}></i>
                                                                    )}
                                                                </span>
                                                            </Tooltip>
                                                        );
                                                    });

                                                case "exportOptionsERAPosting":
                                                    return (
                                                        <Stack direction="row" spacing={1}>
                                                            {(Array.isArray(td.value) ? td.value : []).map((opt, oIdx) => (
                                                                <Tooltip key={oIdx} title={opt.toolTipLabel} arrow>
                                                                    <Box component="span" onClick={(e) => props.exportFunction(e, opt.secondParameter, opt.thirdParameter, opt.toolTipLabel)} sx={{ cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' },
                                                                    '& i': {
                                                                                fontSize: '20px !important',
                                                                                color: opt.fileIconClass.includes('file-icons-red') ? `${theme.palette.error.dark} !important` : `${theme.palette.primary.darker} !important`
                                                                            } }}>
                                                                        <i className={opt.fileIconClass}></i>
                                                                    </Box>
                                                                </Tooltip>
                                                            ))}
                                                        </Stack>
                                                    );

                                                case "plusButton":
                                                    if (props.diagnosisVariable) {
                                                        const exists = checkIsICDAlreadyInList(props.diagnosisVariable, item);
                                                        return exists ? (
                                                            <Tooltip title={<span>Dx <strong>{item[0].value}</strong> already in claim. Click to remove.</span>}>
                                                                <span><ActionButton icon="fa fa-minus" color="secondary" onClick={() => props.removeDiagnosisFromPatientHistory(item)} /></span>
                                                            </Tooltip>
                                                        ) : <ActionButton icon="fas fa-plus" onClick={(e) => props.addDiagnosisFromPatientHistory(e, td.id, item)} />;
                                                    }
                                                    if (props.cptHistoryEnable) {
                                                        const exists = checkIsCPTAlreadyInList(props.procedureRows, item);
                                                        return exists ? (
                                                            <Tooltip title={<span>CPT <strong>{item[0].value}</strong> already in claim.</span>}>
                                                                <span><ActionButton icon="fa fa-minus" disabled /></span>
                                                            </Tooltip>
                                                        ) : <ActionButton icon="fas fa-plus" onClick={(e) => props.addCPTFromPatientHistory(e, td.id, item)} />;
                                                    }
                                                    return <ActionButton icon="fas fa-plus" onClick={(e) => props.onPlusButtonClick(e, td.id)} />;

                                                case "expand": return td?.isExpanded ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />;
                                                case "chip":
                                                    return <Chip label={td?.value} clickable={td.clickable} onClick={td.clickable ? () => props?.onLinkClick(td?.id, td?.name) : undefined} variant="outlined" sx={{ m: 0.5, borderRadius: 2 }} />;
                                                case "multiple_chip":
                                                    return (Array.isArray(td.value) ? td.value : []).map((c) => <Chip key={c.id} label={c.label} clickable={td.clickable} onClick={td.clickable ? () => props?.onLinkClick(td?.id, td?.name) : undefined} variant="outlined" sx={{ m: 0.5, borderRadius: 2 }} />);
                                                
                                                case "workflow_chip": {
                                                    const workflows = Array.isArray(td.value) ? td.value : [];

                                                    if (workflows.length === 0) {
                                                        return <Typography variant="body2">-</Typography>;
                                                    }

                                                   const statusConfig = {
                                                        "to-do": {
                                                            dot: getWorkflowConfig(theme, 'to-do').main,
                                                            bg: getWorkflowConfig(theme, 'to-do').bg,
                                                            border: getWorkflowConfig(theme, 'to-do').border,
                                                            text: getWorkflowConfig(theme, 'to-do').text
                                                        },
                                                        "in progress": {
                                                            dot: getWorkflowConfig(theme, 'in progress').main,
                                                            bg: getWorkflowConfig(theme, 'in progress').bg,
                                                            border: getWorkflowConfig(theme, 'in progress').border,
                                                            text: getWorkflowConfig(theme, 'in progress').text
                                                        },
                                                        "completed": {
                                                            dot: getWorkflowConfig(theme, 'completed').main,
                                                            bg: getWorkflowConfig(theme, 'completed').bg,
                                                            border: getWorkflowConfig(theme, 'completed').border,
                                                            text: getWorkflowConfig(theme, 'completed').text
                                                        },
                                                        "blocked": {
                                                            dot: getWorkflowConfig(theme, 'blocked').main,
                                                            bg: getWorkflowConfig(theme, 'blocked').bg,
                                                            border: getWorkflowConfig(theme, 'blocked').border,
                                                            text: getWorkflowConfig(theme, 'blocked').text
                                                        }
                                                    };

                                                    return (
                                                        <Stack spacing={0.5} direction="column" alignItems="flex-start" mt={2} mb={2}>
                                                            {workflows.map((wf, idx) => {
                                                                let rawStatus = (wf.status || "to-do").toLowerCase().replace("_", " ");
                                                                if (rawStatus === "todo" || rawStatus === "to do") {
                                                                    rawStatus = "to-do"; 
                                                                }
                                                                const formattedName = wf.name.replace(/([a-z])([A-Z])/g, '$1 $2');
                                                                const config = statusConfig[rawStatus] || statusConfig["to-do"];

                                                                return (
                                                                    <Chip
                                                                        key={idx}
                                                                        label={formattedName}
                                                                        variant="outlined"
                                                                        icon={
                                                                            <Box
                                                                                sx={{
                                                                                    width: 8, height: 8, borderRadius: '50%',
                                                                                    bgcolor: config?.dot, ml: 1
                                                                                }}
                                                                            />
                                                                        }
                                                                        sx={{
                                                                            borderRadius: '16px',
                                                                            height: 26,
                                                                            bgcolor: config?.bg,
                                                                            border: `1px solid ${config?.border}`,
                                                                            color: config?.text,
                                                                            fontWeight: 500,
                                                                            justifyContent: 'flex-start',
                                                                            '& .MuiChip-label': {
                                                                                px: 1, fontSize: '0.75rem', textTransform: 'capitalize'
                                                                            },
                                                                            '& .MuiChip-icon': {
                                                                                ml: '8px', mr: '-4px'
                                                                            }
                                                                        }}
                                                                    />
                                                                );
                                                            })}
                                                        </Stack>
                                                    );
                                                }

                                                default:
                                                    if (td.needLink) {
                                                        const value =
                                                            typeof td.value === "string"
                                                                ? td.value
                                                                : String(td.value);

                                                        const link = (
                                                            <CustomLink
                                                                component="button"
                                                                underline="none"
                                                                onClick={() => props?.onLinkClick(td?.id, td?.name)}
                                                            >
                                                                {value}
                                                            </CustomLink>
                                                        );
                                                        return value.length > 20 ? <Tooltip title={value}>{link}</Tooltip> : link;
                                                    }
                                                    if (td.needMyWorkLink) {
                                                        const value =
                                                            typeof td.value === "string"
                                                                ? td.value
                                                                : String(td.value);

                                                        const link = (
                                                            
                                                            <CustomLink
                                                                component="button"
                                                                underline="none"
                                                                onClick={() => props?.onMyWorkLinkClick(td?.custom_claim_id, td?.name,td?.id,td?.work_status,td?.isAssigned)}
                                                            >
                                                                {value}
                                                            </CustomLink>
                                                        );
                                                        return value.length > 20 ? <Tooltip title={value}>{link}</Tooltip> : link;
                                                    }
                                                    if (td.needChip) {
                                                        return td.summaryChip ? <Chips type="summary" label="Summary" size="small" onClick={() => props?.onLinkClick(td?.id, td?.name)} /> : <Chips label="Processing" type="Processing" size="small" color="info" />;
                                                    }
                                                    if (td.name === "status" && td?.commentButton === 'show') {
                                                        return (
                                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                                                <Typography variant="body2">{td.value}</Typography>
                                                                <IconButton size="small" onClick={() => props.onShowMessage(td.id)}><i className="las la-comment"></i></IconButton>
                                                            </Box>
                                                        );
                                                    }
                                                    if (
                                                        (td.name === 'claim_status' ||
                                                            td.name === 'patient_notes' ||
                                                            td.name === 'payer' || td.name === 'notes' ||
                                                            td.name === 'responsible_payer_name') &&
                                                        td.type === 'string'
                                                    ) {
                                                        return (
                                                            <Tooltip title={td.value} placement="left">
                                                                <Typography component="span" sx={{ display: 'block', maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{td.value}</Typography>
                                                            </Tooltip>
                                                        );
                                                    }
                                                    if (td.name === "fee_schedule_name") {
                                                        return (
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <Typography sx={{ fontSize: 12 }}>{td.value}</Typography>
                                                                {td.clone_completed && <Chip label="In Progress..." size="small" sx={{ borderRadius: 1, bgcolor: "#E7FFE8", color: "#20744A", height: 22, fontWeight: "bold" }} />}
                                                            </Box>
                                                        );
                                                    }
                                                    if (td.name === "responsible") {
                                                        const parts = td?.value?.name?.split("||") || [];
                                                        return parts[1] || parts[2] ? (
                                                            <Tooltip title={<Box><strong>Payer ID: {parts[1]?.toUpperCase() || 'No Details'}</strong><br /><strong>Payer Ph: {parts[2]?.toUpperCase() || 'No Details'}</strong></Box>}>
                                                                <Typography component="span" sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline', textTransform: 'uppercase' }}>{parts[0]}</Typography>
                                                            </Tooltip>
                                                        ) : <Typography component="span">{selfPayPatientIndex.includes(index) ? 'Patient' : parts[0]?.toUpperCase()}</Typography>;
                                                    }
                                                    if(td.name === "responsible_payer_name"){
                                                        return <Typography component="span">{td.value}</Typography>;
                                                    }
                                                    if (td.name === "remarks") {
                                                        const content = <Typography sx={{ display: 'inline-block', maxWidth: '250px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'bottom' }}>{td.value}</Typography>;
                                                        return String(td.value || "").length > 30 ? <Tooltip title={td.value}>{content}</Tooltip> : content;
                                                    }
                                                    if (td.name === "responsible_payer_name") {
                                                        return <Tooltip title={td.value}>
                                                            <Typography
                                                                component="span"
                                                                sx={{
                                                                    cursor: 'pointer',
                                                                    textTransform: 'uppercase',
                                                                    display: '-webkit-box',
                                                                    WebkitLineClamp: 2,
                                                                    WebkitBoxOrient: 'vertical',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    wordBreak: 'break-word',
                                                                    maxWidth: '200px',
                                                                }}
                                                            >
                                                                {td.value}
                                                            </Typography>
                                                        </Tooltip>;
                                                    }
                                                    if (td.value || td.value === 0) {
                                                        return (
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    wordBreak: "break-word",
                                                                    overflowWrap: "anywhere",
                                                                    whiteSpace: "normal"
                                                                }}
                                                            >
                                                                {td.value}
                                                            </Typography>
                                                        );
                                                    }

                                            }
                                        })()}
                                    </StyledTableCell>
                                );
                            })}
                        </StyledTableRow>
                        {props?.rowExpansion === "allRows" && (
                            <TableRow sx={{ display: item[0]?.isExpanded ? 'table-row' : 'none' }}>
                                <TableCell colSpan={props?.colSpanNum || 1} sx={{ p: 2, bgcolor: theme.palette.background.default }}>
                                    {item[0].expandData?.dataObject}
                                </TableCell>
                            </TableRow>
                        )}
                    </React.Fragment>
                );
            })}
                <CustomizedSmallDialogs
                    showModal={errorModal.open}
                    setShowModalWindow={setErrorModal}
                    header={"Error"}
                    type="alert"
                    alertOK={handleCloseModal}
                    resetForm={handleCloseModal}
                >
                    {errorModal.message || "An unexpected error occurred. Please contact support."}
                </CustomizedSmallDialogs>
            
        </MuiTableBody>
    );
};

export default TableBody;